import { useState } from "react";
import { motion } from "framer-motion";
import { UserData } from "@/pages/Index";

interface PersonalProfileProps {
  userData: UserData;
  onUpdate: (data: Partial<UserData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const concernOptions = [
  { label: "Fijne lijntjes & rimpels", value: "wrinkles", emoji: "🧴" },
  { label: "Verlies van stevigheid", value: "firmness", emoji: "🤲" },
  { label: "Doffe, ongelijke teint", value: "dullness", emoji: "😔" },
  { label: "Droge, dunne huid", value: "dryness", emoji: "🏜️" },
  { label: "Pigmentvlekken", value: "pigment", emoji: "🎨" },
  { label: "Verslapping kaak/hals", value: "sagging", emoji: "📉" },
];

const PersonalProfile = ({ userData, onUpdate, onNext, onBack }: PersonalProfileProps) => {
  const toggleConcern = (value: string) => {
    const current = userData.skinConcerns;
    const updated = current.includes(value)
      ? current.filter((c) => c !== value)
      : [...current, value];
    onUpdate({ skinConcerns: updated });
  };

  const selectedLabels = concernOptions
    .filter((c) => userData.skinConcerns.includes(c.value))
    .map((c) => c.label);

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-foreground/80 font-sans text-lg md:text-xl mb-5 md:mb-8 hover:text-foreground transition-colors cursor-pointer px-3 py-2 -ml-3 rounded-lg hover:bg-secondary/50"
      >
        <span className="text-xl">←</span> Terug
      </button>

      <div className="text-center">
        <h2 className="text-2xl md:text-4xl font-serif font-medium text-foreground mb-2 leading-tight">
          Wat wil jij het meest verbeteren?
        </h2>
        <p className="text-foreground/80 font-sans text-lg mb-5 md:mb-8">
          Selecteer alles dat van toepassing is
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3 mb-5 md:mb-8">
          {concernOptions.map((concern, i) => (
            <motion.button
              key={concern.value}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.06 * i }}
              onClick={() => toggleConcern(concern.value)}
              className={`quiz-option text-left flex items-center gap-3 ${
                userData.skinConcerns.includes(concern.value) ? "quiz-option-selected" : ""
              }`}
            >
              <span className="text-2xl flex-shrink-0 w-8 text-center">{concern.emoji}</span>
              <span className="text-foreground text-lg flex-1">{concern.label}</span>
              {userData.skinConcerns.includes(concern.value) && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="text-primary font-bold flex-shrink-0"
                >
                  ✓
                </motion.span>
              )}
            </motion.button>
          ))}
        </div>

        {/* Summary of their problem before showing the solution */}
        {userData.skinConcerns.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="insight-card mb-5 md:mb-8 text-left">
              <p className="font-serif text-xl md:text-2xl text-foreground mb-3 leading-snug">
                Jouw huidprofiel is compleet.
              </p>
              <p className="font-sans text-lg text-foreground/80 leading-relaxed mb-4">
                Je ervaart <span className="text-primary font-semibold">{selectedLabels.join(", ").replace(/, ([^,]*)$/, " en $1")}</span> en je weet nu dat dit direct samenhangt met dalend collageen.
              </p>
              <p className="font-sans text-lg text-foreground/80 font-medium">
                Wij gaan berekenen welke oplossing het beste past in jouw situatie.
              </p>
            </div>

            <button onClick={onNext} className="btn-cta">
              Toon mij de oplossing
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PersonalProfile;
